import React, { createContext, useContext, useMemo } from 'react';
import { useTabPaneContext } from './TabPaneContext';
import { useDeferredEffectActive } from './useDeferredEffectActive';

const ViewActivityContext = createContext(true);

interface ViewActivityProviderProps {
  active: boolean;
  children: React.ReactNode;
}

/**
 * Propaga a atividade de uma view aninhada. Providers aninhados nunca podem
 * reativar um pai oculto: a atividade efetiva é sempre pai && filho.
 *
 * A visibilidade visual continua imediata no pai (RetainedTabPanel etc.);
 * aqui só a reativação de efeitos é adiada com startTransition.
 */
export const ViewActivityProvider: React.FC<ViewActivityProviderProps> = ({
  active,
  children,
}) => {
  const parentActive = useContext(ViewActivityContext);
  const effectActive = useDeferredEffectActive(active);
  const effectiveActive = parentActive && effectActive;
  const value = useMemo(() => effectiveActive, [effectiveActive]);

  return (
    <ViewActivityContext.Provider value={value}>
      {children}
    </ViewActivityContext.Provider>
  );
};

/**
 * Indica se a rota keep-alive e todas as views internas ancestrais estão ativas.
 */
export const useIsActiveView = (): boolean => {
  const paneContext = useTabPaneContext();
  const viewActive = useContext(ViewActivityContext);
  return viewActive && (paneContext?.isPaneActive ?? true);
};
